import { useEffect, useState } from "react";
import type { MedicalEvent, PtProgramPayload } from "@medassist/shared";
import { listPtPrograms, ApiError } from "../../../lib/api.js";
import { ActivityIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function programLabel(payload: PtProgramPayload): string {
  return payload.suspectedDiagnosis ?? payload.bodyRegion ?? "PT Program";
}

export function PhysicalTherapyWidget({ patient, events, onNavigate, onError }: WidgetProps) {
  const [programs, setPrograms] = useState<MedicalEvent[] | null>(null);

  useEffect(() => {
    listPtPrograms(patient.patientId)
      .then(setPrograms)
      .catch((err) => onError(err instanceof ApiError ? err.message : "Couldn't load PT programs"));
  }, [patient.patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = todayKey();
  const sessionsTodayIds = new Set(
    events
      // A confirmed "didn't do it" session (CATCHUP_WIDGETS_PLAN.md D-CU-10) counts
      // as handled for reminders, never as done here.
      .filter(
        (e) =>
          e.eventType === "pt_session" &&
          e.occurredAt.slice(0, 10) === today &&
          !(e.payload as Record<string, unknown>).skipped
      )
      .map((e) => (e.payload as Record<string, unknown>).programEventId as string)
  );

  const activePrograms = programs ?? [];

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <ActivityIcon size={16} className="text-green-600 dark:text-green-400" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Physical therapy</h2>
        <button onClick={() => onNavigate("physical-therapy")} className="ml-auto text-xs text-(--accent) hover:underline">
          View
        </button>
      </div>
      {programs === null ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
      ) : activePrograms.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No PT programs yet</p>
      ) : (
        <div className="space-y-2">
          {activePrograms.slice(0, 3).map((p) => {
            const payload = p.payload as unknown as PtProgramPayload;
            const doneToday = sessionsTodayIds.has(p.eventId);
            return (
              <div key={p.eventId} className="flex items-center justify-between text-sm">
                <span className="truncate text-gray-700 dark:text-gray-300">{programLabel(payload)}</span>
                {doneToday ? (
                  <span className="ml-2 shrink-0 rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
                    Done today
                  </span>
                ) : (
                  <button
                    onClick={() => onNavigate("physical-therapy")}
                    className="ml-2 shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-green-100 dark:hover:bg-green-900/40"
                  >
                    Log session
                  </button>
                )}
              </div>
            );
          })}
          {activePrograms.length > 3 && (
            <p className="text-xs text-gray-400 dark:text-gray-500">+{activePrograms.length - 3} more</p>
          )}
        </div>
      )}
    </section>
  );
}
